import React from 'react';
import { AgnesBadge, AgnesButton, AgnesCheckbox, formatCnpj } from '@agnes/ui';
import { ChevronRight } from 'lucide-react';
import { BaseComponentProps } from '@/components/BaseComponentProps';
import { ExportButton } from '@/components/ExportButton';
import { SearchInput } from '@/components/SearchInput';
import { SectionCard } from '@/components/ui/SectionCard';
import { SITUACOES_RUBRICA } from '@/constants/ConstantSituacaoRubrica';
import { exportarCsv } from '@/lib/csv';
import { Rubrica } from '@/types/rubricas/Rubrica';
import { CAMPOS_INCIDENCIA, formatarTipo } from '@/pages/folha-pagamento/acao-em-massa/acaoEmMassa';
import { BadgeGrauExposicaoRisco } from '@/components/analise/BadgeGrauExposicaoRisco';
import { AnaliseGrauExposicaoRisco } from '@/types/analise/AnaliseGrauExposicaoRisco';

export interface EtapaRelacaoRubricasProps extends BaseComponentProps {
  rubricas: Rubrica[];
  incluidas: ReadonlySet<number>;
  onIncluidasChange: (incluidas: Set<number>) => void;
  origem: string;
  nomeEmpregador: (cnpj: string | null) => string | undefined;
  onCancelar: () => void;
  onAvancar: () => void;
}

const TH = 'whitespace-nowrap p-3 font-semibold';
const TD = 'p-3 align-middle';

function textoBusca(rubrica: Rubrica) {
  return [rubrica.codigoRubrica, rubrica.descricao, rubrica.naturezaCodigo, rubrica.naturezaNome]
    .join(' ')
    .toLowerCase();
}

export function EtapaRelacaoRubricas({
  rubricas,
  incluidas,
  onIncluidasChange,
  origem,
  nomeEmpregador,
  onCancelar,
  onAvancar,
  dataQa = 'acao-massa-relacao',
}: EtapaRelacaoRubricasProps) {
  const [buscaDigitada, setBuscaDigitada] = React.useState('');
  const [busca, setBusca] = React.useState('');

  const visiveis = React.useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return termo ? rubricas.filter((rubrica) => textoBusca(rubrica).includes(termo)) : rubricas;
  }, [busca, rubricas]);

  const todasVisiveis =
    visiveis.length > 0 && visiveis.every((r) => incluidas.has(r.idAnaliseRubrica));
  const algumaVisivel = visiveis.some((r) => incluidas.has(r.idAnaliseRubrica));

  const alternarVisiveis = () => {
    const next = new Set(incluidas);
    visiveis.forEach((r) =>
      todasVisiveis ? next.delete(r.idAnaliseRubrica) : next.add(r.idAnaliseRubrica),
    );
    onIncluidasChange(next);
  };

  const alternar = (id: number) => {
    const next = new Set(incluidas);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onIncluidasChange(next);
  };

  const exportar = () =>
    exportarCsv(
      'rubricas-acao-em-massa.csv',
      [
        'Empregador',
        'CNPJ',
        'Id. tabela',
        'Código',
        'Natureza',
        'Descrição',
        'Tipo',
        'Situação',
        'CP',
        'IRRF',
        'FGTS',
        'CPRP',
        'PIS',
        'Grau de risco',
      ],
      rubricas
        .filter((r) => incluidas.has(r.idAnaliseRubrica))
        .map((r) => [
          nomeEmpregador(r.cnpjEmpregador),
          r.cnpjEmpregador,
          r.idTabela,
          r.codigoRubrica,
          r.naturezaCodigo,
          r.descricao,
          formatarTipo(r.tipoNome),
          r.situacao,
          r.cp,
          r.irrf,
          r.fgts,
          r.cprp,
          r.pis,
          r.grauRisco,
        ]),
    );

  return (
    <SectionCard data-qa={dataQa} className="overflow-hidden p-0">
      <div className="border-b border-agnes-border bg-agnes-bg-subtle px-5 py-4">
        <h2 className="text-lg font-semibold text-agnes-primary">Dados a serem alterados</h2>
        <p className="mt-1 text-sm text-agnes-text-muted">
          Lista de rubricas que serão afetadas pela ação — origem: {origem}.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-5 py-4">
        <div className="min-w-72 flex-1">
          <SearchInput
            value={buscaDigitada}
            onValueChange={setBuscaDigitada}
            onSearch={setBusca}
            placeholder="Pesquisar por código ou descrição da rubrica"
          />
        </div>
        <ExportButton
          dataQa={`${dataQa}-exportar`}
          variant="outlined"
          disabled={incluidas.size === 0}
          onClick={exportar}
        />
      </div>

      <div
        data-qa={`${dataQa}-tabela-container`}
        className="max-h-[50vh] overflow-auto"
        role="region"
        aria-label="Rubricas da ação em massa"
        tabIndex={0}
      >
        <table
          data-qa={`${dataQa}-tabela`}
          className="w-full min-w-[1100px] text-left text-sm text-agnes-text-tables"
        >
          <thead className="sticky top-0 z-10 bg-agnes-bg-table-header text-xs text-agnes-text-secondary">
            <tr>
              <th scope="col" className="w-11 p-3">
                <AgnesCheckbox
                  dataQa={`${dataQa}-selecao-todas`}
                  aria-label="Selecionar todas as rubricas"
                  checked={todasVisiveis ? true : algumaVisivel ? 'indeterminate' : false}
                  onCheckedChange={alternarVisiveis}
                />
              </th>
              {[
                'Empregador',
                'Id. tabela',
                'Código',
                'Natureza',
                'Descrição',
                'Tipo',
                'Situação',
              ].map((coluna) => (
                <th key={coluna} scope="col" className={TH}>
                  {coluna}
                </th>
              ))}
              {CAMPOS_INCIDENCIA.map(({ key }) => (
                <th key={key} scope="col" className={`${TH} text-center`}>
                  {key.toUpperCase()}
                </th>
              ))}
              <th scope="col" className={TH}>
                Grau de risco
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[var(--tp-border)]">
            {visiveis.map((rubrica) => {
              const id = rubrica.idAnaliseRubrica;
              const itemQa = `${dataQa}-item-${id}`;
              const situacao = SITUACOES_RUBRICA.find((s) => s.value === rubrica.situacao);
              return (
                <tr
                  key={id}
                  data-qa={itemQa}
                  className={incluidas.has(id) ? 'bg-agnes-primary/5' : undefined}
                >
                  <td className={TD}>
                    <AgnesCheckbox
                      dataQa={`${itemQa}-selecao`}
                      aria-label={`Selecionar rubrica ${rubrica.codigoRubrica ?? id}`}
                      checked={incluidas.has(id)}
                      onCheckedChange={() => alternar(id)}
                    />
                  </td>
                  <td className={TD}>
                    <p className="text-xs font-semibold uppercase text-agnes-text-primary">
                      {nomeEmpregador(rubrica.cnpjEmpregador) ?? '—'}
                    </p>
                    <p className="text-xs text-agnes-text-muted">
                      {rubrica.cnpjEmpregador ? formatCnpj(rubrica.cnpjEmpregador) : '—'}
                    </p>
                  </td>
                  <td className={TD}>{rubrica.idTabela || '—'}</td>
                  <td className={`${TD} font-bold text-agnes-text-primary`}>
                    {rubrica.codigoRubrica || '—'}
                  </td>
                  <td className={TD}>{rubrica.naturezaCodigo ?? '—'}</td>
                  <td className={TD}>
                    <p className="font-semibold text-agnes-text-primary">
                      {rubrica.descricao || '—'}
                    </p>
                    <p className="text-xs text-agnes-text-muted">{rubrica.naturezaNome || '—'}</p>
                  </td>
                  <td className={TD}>{formatarTipo(rubrica.tipoNome)}</td>
                  <td className={TD}>
                    {situacao ? (
                      <AgnesBadge
                        size="sm"
                        variant={situacao.value === 'ATIVA' ? 'success' : 'secondary'}
                      >
                        {situacao.label}
                      </AgnesBadge>
                    ) : (
                      rubrica.situacao || '—'
                    )}
                  </td>
                  {CAMPOS_INCIDENCIA.map(({ key }) => (
                    <td key={key} className={`${TD} text-center`}>
                      {rubrica[key] || '—'}
                    </td>
                  ))}
                  <td className={TD}>
                    {rubrica.grauRisco ? (
                      <BadgeGrauExposicaoRisco
                        dataQa={`${itemQa}-risco`}
                        grau={rubrica.grauRisco as AnaliseGrauExposicaoRisco}
                      />
                    ) : (
                      <span data-qa={`${itemQa}-risco`}>—</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {visiveis.length === 0 && (
              <tr>
                <td
                  data-qa={`${dataQa}-vazio`}
                  colSpan={14}
                  className="p-8 text-center text-agnes-text-muted"
                >
                  Nenhuma rubrica encontrada
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-agnes-border px-5 py-4">
        <span data-qa={`${dataQa}-total`} role="status" className="text-agnes-text-muted">
          {incluidas.size.toLocaleString('pt-BR')} de {rubricas.length.toLocaleString('pt-BR')}{' '}
          rubricas selecionadas
        </span>
        <div className="flex gap-3">
          <AgnesButton dataQa={`${dataQa}-cancelar`} variant="text" onClick={onCancelar}>
            Cancelar
          </AgnesButton>
          <AgnesButton
            dataQa={`${dataQa}-avancar`}
            variant="primary"
            startIcon={<ChevronRight className="h-4 w-4" />}
            disabled={incluidas.size === 0}
            onClick={onAvancar}
          >
            Confirmar e avançar
          </AgnesButton>
        </div>
      </div>
    </SectionCard>
  );
}
